import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

// others
import { useDebounce } from 'use-debounce'

// components
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import {
  PaymentSource,
  PaymentsPayload,
  PaymentType,
  useFetchAllPaymentsLazyQuery,
  useReversePaymentMutation
} from '../../../../generated'
import {
  CLIENT_PAYMENT_HEADERS,
  FILTER_DATE_FORMAT,
  FIRM_PAYMENT_HEADERS,
  GRAPHQL_QUERY_POLICY,
  PAGE_LIMIT,
  QUERY_PARAMS,
  VENDOR_PAYMENT_HEADERS
} from '../../../constants'
import { AppContext, AuthContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { PaymentFilterType, ProcessedPayment } from '../../../interfaceTypes/common'
import { CardBox, CustomTabButton } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { formatCurrency, getProperPaymentMethod, hasAdminAccess } from '../../../utils'
import { REFUND_PAYMENT_METHODS } from '../../../utils/refund'
import { Alert } from '../../common/Alert'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { ContentHead } from '../../common/ContentHead'
import { EmptyData } from '../../common/EmptyData'
import PaymentFilters from './PaymentFilters'
import PaymentsListing, { ClientPayments, FirmPayments, VendorPayments } from './PaymentListing'
import EditPaymentModal from './EditPaymentModal'
import ViewPaymentModal from './ViewPaymentModal'

const PAGE_SIZE_OPTIONS = [10, 25, 50]

const Payments = () => {
  const [payments, setPayments] = useState<PaymentsPayload['payments']>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const fromParam = searchParams.get(QUERY_PARAMS.FROM)
  const toParam = searchParams.get(QUERY_PARAMS.TO)
  const accountIdParam = searchParams.get(QUERY_PARAMS.ACCOUNT_ID)
  const paymentTabParam = searchParams.get(QUERY_PARAMS.PAYMENT_TAB)
  const searchParam = searchParams.get(QUERY_PARAMS.SEARCH)
  const slipNumberParam = searchParams.get(QUERY_PARAMS.SLIP_NUMBER) || undefined
  const officeIdParam = searchParams.get(QUERY_PARAMS.OFFICE_ID) || undefined
  const pageSizeParam = searchParams.get(QUERY_PARAMS.PAGE_SIZE)
  const reconciledParam = searchParams.get(QUERY_PARAMS.RECONCILED)
  const matterParam = searchParams.get(QUERY_PARAMS.MATTER)
  const caseIdParam = matterParam && matterParam !== QUERY_PARAMS.ALL ? matterParam : undefined

  const [paymentFilter, setPaymentFilter] = useState<PaymentFilterType>(
    (paymentTabParam as PaymentFilterType) ?? 'client'
  )

  const [pageSize, setPageSize] = useState<number>(pageSizeParam ? Number(pageSizeParam) : PAGE_LIMIT)

  const fromDate = useMemo(
    () =>
      fromParam
        ? dayjs(fromParam!).format(FILTER_DATE_FORMAT)
        : dayjs(new Date()).subtract(1, 'month').format(FILTER_DATE_FORMAT),
    [fromParam]
  )
  const toDate = useMemo(
    () => (toParam ? dayjs(toParam!).format(FILTER_DATE_FORMAT) : dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)),
    [toParam]
  )

  const { searchValue, currentPage, dispatch } = useContext(AppContext)
  const [debouncedSearch] = useDebounce(searchValue, 500)

  // Initialize search from URL so deep links with ?search=... load with that search
  useEffect(() => {
    if (searchParam != null && searchParam !== searchValue) {
      dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: searchParam })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam])

  // Sync search term to URL so sharing or revisiting the page preserves the search
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (searchValue) {
      next.set(QUERY_PARAMS.SEARCH, searchValue)
      setSearchParams(next, { replace: true })
    } else if (searchParam == null) {
      // Only remove search from URL when user cleared search, not on initial load with no param
      next.delete(QUERY_PARAMS.SEARCH)
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  const [fetchAllPayments, { loading, refetch }] = useFetchAllPaymentsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchAllPayments } = data || {}
      const { payments, pagination } = fetchAllPayments || {}
      setPayments(payments as PaymentsPayload['payments'])
      if (pagination) {
        const { totalPages, totalCount } = pagination

        dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages || 1 })
        dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount || 1 })
      }
    }
  })

  useEffect(() => {
    fetchAllPayments({
      variables: {
        fetchAllPaymentsInput: {
          paginationOptions: {
            page: currentPage,
            limit: pageSize
          },
          keyword: debouncedSearch,
          filters: {
            excludeGeneralLedgerPayments: true,
            startDate: fromDate,
            endDate: toDate,
            accountId: accountIdParam,
            paymentOf: paymentFilter,
            slipNumber: slipNumberParam,
            officeId: officeIdParam ? Number(officeIdParam) : undefined,
            reconciled: reconciledParam === 'true' ? true : undefined,
            caseId: caseIdParam
          }
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearch,
    fromDate,
    toDate,
    accountIdParam,
    paymentFilter,
    slipNumberParam,
    officeIdParam,
    pageSize,
    reconciledParam,
    caseIdParam
  ])

  useEffect(() => {
    return () => {
      dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      return dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
  }, [dispatch])

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

  const isEmpty = !loading && payments?.length === 0

  const handleEditPayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId)
    setIsEditModalOpen(true)
  }

  const handleViewPayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId)
    setIsViewModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setSelectedPaymentId(null)
  }

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false)
    setSelectedPaymentId(null)
  }

  const handleEditSuccess = () => {
    refetch()
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    const next = new URLSearchParams(searchParams)
    next.set(QUERY_PARAMS.PAGE_SIZE, String(newSize))
    setSearchParams(next, { replace: true })
  }

  const processPayments = useCallback((payments: PaymentsPayload['payments']) => {
    let processedPayments: ProcessedPayment[] = []
    const linkedPaymentIds = new Set<string>()

    payments?.forEach(payment => {
      const { linkedPayment } = payment || {}
      const isDebit = payment.paymentType === PaymentType.Debit
      const isCredit = payment.paymentType === PaymentType.Credit

      if (linkedPayment?.id) {
        const sourcePayment = isDebit ? payment : linkedPayment
        const destinationPayment = isDebit ? linkedPayment : payment

        processedPayments.push({
          ...payment,
          sourceAccount: sourcePayment.account,
          destinationAccount: destinationPayment.account
        })

        linkedPaymentIds.add(linkedPayment.id)
      } else {
        processedPayments.push({
          ...payment,
          sourceAccount: isDebit ? payment.account : null,
          destinationAccount: isCredit ? payment.account : null
        })
      }
    })

    return processedPayments.filter(payment => !linkedPaymentIds.has(payment.id))
  }, [])

  const processedPayments = processPayments(payments || [])

  // Refunds appear in the client tab alongside client payments, so the edit form is relabelled for
  // them. The fields it offers are the same either way — everything an edit must not touch (amount,
  // date, bank account) is already read-only.
  const isSelectedPaymentRefund =
    processedPayments.find(payment => payment.id === selectedPaymentId)?.paymentSource === PaymentSource.RefundPayment

  const { currentUser } = useContext(AuthContext)
  const isAdmin = !!hasAdminAccess(currentUser)
  const deleteTarget = deleteTargetId ? processedPayments.find(payment => payment.id === deleteTargetId) : undefined

  const [reversePaymentMutation, { loading: deletingPayment }] = useReversePaymentMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      if (data?.reversePayment?.response?.status === 200) {
        Alert.success('Payment deleted successfully')
        setDeleteTargetId(null)
        refetch()
      }
    }
  })

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return
    reversePaymentMutation({ variables: { reversePaymentInput: { paymentId: deleteTargetId } } })
  }

  const getDeleteConfirmationMessage = (payment: ProcessedPayment): string =>
    [
      `Amount: ${formatCurrency(payment.amount)}`,
      `Payment Method: ${getProperPaymentMethod(payment.paymentMethod)}`,
      `Date: ${dayjs(payment.date).format('MMM DD, YYYY')}`,
      '',
      'This action will:',
      '- Delete this firm payment and reverse the bank account balance',
      '- Create reversing journal entries for the bank and GL accounts',
      '',
      'This cannot be undone.'
    ].join('\n')

  const totalPaymentSum = useMemo(() => {
    return processedPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
  }, [processedPayments])

  const getData = useCallback(() => {
    switch (paymentFilter) {
      case 'client': {
        return {
          headers: CLIENT_PAYMENT_HEADERS,
          component: (
            <ClientPayments payments={processedPayments} onEdit={handleEditPayment} onView={handleViewPayment} />
          )
        }
      }
      case 'vendor': {
        return {
          headers: VENDOR_PAYMENT_HEADERS,
          component: (
            <VendorPayments payments={processedPayments} onEdit={handleEditPayment} onView={handleViewPayment} />
          )
        }
      }
      default: {
        return {
          headers: FIRM_PAYMENT_HEADERS,
          component: (
            <FirmPayments
              payments={processedPayments}
              onEdit={handleEditPayment}
              onView={handleViewPayment}
              onDelete={setDeleteTargetId}
              isAdmin={isAdmin}
            />
          )
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedPayments, paymentFilter, isAdmin])

  const paymentData = getData()

  return (
    <CardBox marginTop={3}>
      <ContentHead title={'Payments'} />

      <Box padding="20px 0px 20px 15px">
        <CustomTabButton
          isLeftItem
          className={paymentFilter === 'client' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          onClick={() => {
            searchParams.set(QUERY_PARAMS.PAYMENT_TAB, 'client')
            setSearchParams(searchParams)
            setPaymentFilter('client')
          }}
        >
          Client Payments
        </CustomTabButton>

        <CustomTabButton
          isCenterItem
          className={paymentFilter === 'vendor' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          onClick={() => {
            searchParams.set(QUERY_PARAMS.PAYMENT_TAB, 'vendor')
            setSearchParams(searchParams)
            setPaymentFilter('vendor')
          }}
        >
          Vendor Payments
        </CustomTabButton>

        <CustomTabButton
          className={paymentFilter === 'firm' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          onClick={() => {
            searchParams.set(QUERY_PARAMS.PAYMENT_TAB, 'firm')
            setSearchParams(searchParams)
            setPaymentFilter('firm')
          }}
        >
          Firm Payments
        </CustomTabButton>
      </Box>

      <Box mx={2} mb={3}>
        <PaymentFilters loading={loading} />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mx={2} mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            Total Payment:{' '}
            <Typography component="span" fontWeight={700} color="primary.main">
              ${totalPaymentSum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
              size="small"
              sx={{ fontSize: 14 }}
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title={'No Payments!'} />
        </Box>
      ) : (
        <PaymentsListing
          payments={payments}
          loading={loading}
          refetch={refetch}
          paymentData={paymentData}
          onEdit={handleEditPayment}
          onView={handleViewPayment}
        />
      )}

      {selectedPaymentId && (
        <>
          <EditPaymentModal
            paymentId={selectedPaymentId}
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
            {...(isSelectedPaymentRefund && {
              paymentMethods: REFUND_PAYMENT_METHODS,
              title: 'Edit Refund',
              submitLabel: 'Update Refund',
              successMessage: 'Refund updated successfully',
              accountLabel: 'Refunded From'
            })}
          />
          <ViewPaymentModal paymentId={selectedPaymentId} isOpen={isViewModalOpen} onClose={handleCloseViewModal} />
        </>
      )}

      {deleteTarget && (
        <ConfirmationModal
          isOpen={!!deleteTarget}
          title="Delete Firm Payment"
          message={getDeleteConfirmationMessage(deleteTarget)}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          isLoading={deletingPayment}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTargetId(null)}
        />
      )}
    </CardBox>
  )
}

export default Payments
